import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import ScrollFadeWrapper from "@/components/ui/scroll-fade-wrapper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import Typography from "@/components/ui/typography";
import InvoiceDrawer from "@/features/invoices/components/invoice/InvoiceDrawer";
import {
  useCancelRecurringInvoice,
  useRecurringInvoice,
  useUpdateRecurringInvoice,
} from "@/features/invoices/hooks/useRecurringInvoices";
import {
  DueDateType,
  FrequencyUnit,
  RecurringInvoiceWithInstances,
} from "@/features/invoices/types/recurring";
import { track } from "@/utils/mixpanel";

interface RecurringInvoiceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recurringInvoiceId: string | null;
  onInvoiceClick?: (invoiceId: string) => void;
  startInEdit?: boolean;
}

const recurringConfigSchema = z
  .object({
    description: z.string().optional(),
    dueDateType: z.enum(["immediate", "net_days"]),
    dueDateDays: z.number().int().min(0).max(180),
  })
  .refine(
    (data) => {
      // When dueDateType is net_days, dueDateDays must be at least 1
      if (data.dueDateType === "net_days" && data.dueDateDays < 1) {
        return false;
      }
      return true;
    },
    {
      message: "Number of days must be at least 1",
      path: ["dueDateDays"],
    },
  );

type RecurringConfigValues = z.infer<typeof recurringConfigSchema>;

const formatCurrencyAmount = (amount: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

const formatFrequency = (frequency: number, unit: FrequencyUnit) => {
  const unitLabel = frequency === 1 ? "month" : "months";
  return `Every ${frequency} ${unitLabel}`;
};

const formatDate = (timestamp?: number | null) => {
  if (!timestamp || Number.isNaN(timestamp)) return "—";
  return format(new Date(timestamp * 1000), "MMM d, yyyy");
};

const getRecurringStatusBadge = (
  status: RecurringInvoiceWithInstances["status"],
) => {
  switch (status) {
    case "active":
      return (
        <Badge color="green" variant="opaque">
          Active
        </Badge>
      );
    case "canceled":
      return (
        <Badge color="grey" variant="opaque">
          Canceled
        </Badge>
      );
    case "completed":
      return (
        <Badge color="blue" variant="opaque">
          Completed
        </Badge>
      );
    case "paused":
      return (
        <Badge color="yellow" variant="opaque">
          Paused
        </Badge>
      );
    default:
      return (
        <Badge color="grey" variant="opaque">
          {status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
        </Badge>
      );
  }
};

const getEndDateLabel = (recurringInvoice: RecurringInvoiceWithInstances) => {
  if (recurringInvoice.status === "canceled") {
    return `Canceled ${formatDate(recurringInvoice.canceledAt)}`;
  }
  if (recurringInvoice.status === "completed") {
    return `Completed ${formatDate(recurringInvoice.completedAt)}`;
  }
  if (recurringInvoice.endCondition === "after_count") {
    return `After ${recurringInvoice.endAfterCount || 0} invoices`;
  }
  return "Never";
};

const getPaymentDueLabel = (dueDateType: DueDateType, dueDateDays: number) => {
  if (dueDateType === "immediate") {
    return "On receipt";
  }
  return `Within ${dueDateDays} days`;
};

const RecurringInvoiceDrawer = ({
  open,
  onOpenChange,
  recurringInvoiceId,
  onInvoiceClick,
  startInEdit = false,
}: RecurringInvoiceDrawerProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const hasOpenedRef = useRef(false);
  const updateMutation = useUpdateRecurringInvoice();
  const cancelMutation = useCancelRecurringInvoice();
  const {
    data: recurringInvoice,
    isLoading,
    error,
  } = useRecurringInvoice(open ? recurringInvoiceId : null);

  const form = useForm<RecurringConfigValues>({
    resolver: zodResolver(recurringConfigSchema),
    defaultValues: {
      description: "",
      dueDateType: "immediate",
      dueDateDays: 0,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setShowCancelConfirm(false);
      hasOpenedRef.current = false;
      return;
    }
    if (!hasOpenedRef.current) {
      setIsEditing(startInEdit);
      setShowCancelConfirm(false);
      hasOpenedRef.current = true;
    }
    if (recurringInvoice) {
      form.reset({
        description: recurringInvoice.description || "",
        dueDateType: recurringInvoice.dueDateType || "immediate",
        dueDateDays: recurringInvoice.dueDateDays || 0,
      });
    }
  }, [open, recurringInvoice, form, startInEdit]);

  const issuedInvoices = useMemo(() => {
    if (!recurringInvoice?.issuedInvoices) return [];
    return [...recurringInvoice.issuedInvoices].sort(
      (a, b) => (b.date || 0) - (a.date || 0),
    );
  }, [recurringInvoice?.issuedInvoices]);

  const handleSave = async (values: RecurringConfigValues) => {
    if (!recurringInvoice) return;
    await updateMutation.mutateAsync({
      id: recurringInvoice.id,
      data: {
        description: values.description,
        dueDateType: values.dueDateType,
        dueDateDays: values.dueDateType === "net_days" ? values.dueDateDays : 0,
      },
    });
    setIsEditing(false);
  };

  const handleCancel = async () => {
    if (!recurringInvoice) return;
    await cancelMutation.mutateAsync(recurringInvoice.id);
    setShowCancelConfirm(false);
  };

  const isActive = recurringInvoice?.status === "active";

  return (
    <InvoiceDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Recurring Invoice"
      subtitle={recurringInvoice?.customerName || "Loading..."}
      statusBadge={
        recurringInvoice
          ? getRecurringStatusBadge(recurringInvoice.status)
          : null
      }
      footer={
        isEditing ? (
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">
              Changes apply to future invoices only.
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Discard changes
              </Button>
              <Button
                type="button"
                onClick={form.handleSubmit(handleSave)}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <div className="space-y-2">
          <Typography size="sh2" className="text-[#1e1e24]">
            Error Loading Recurring Invoice
          </Typography>
          <p className="text-sm text-muted-foreground">
            There was an error loading the recurring invoice. Please try again.
          </p>
        </div>
      ) : !recurringInvoice ? (
        <div className="space-y-2">
          <Typography size="sh2" className="text-[#1e1e24]">
            Recurring Invoice Not Found
          </Typography>
          <p className="text-sm text-muted-foreground">
            The recurring invoice could not be loaded.
          </p>
        </div>
      ) : isEditing ? (
        /* Edit Mode */
        <Form {...form}>
          <form className="space-y-6">
            {/* Locked fields header */}
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-[#767677] font-semibold">
                    Customer
                  </div>
                  <div className="text-sm font-medium text-[#1e1e24]">
                    {recurringInvoice.customerName || "Customer"}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-[#767677] font-semibold">
                    Amount
                  </div>
                  <div className="text-sm font-medium text-[#1e1e24]">
                    {formatCurrencyAmount(
                      recurringInvoice.amount || 0,
                      recurringInvoice.currency || "usd",
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-[#767677] font-semibold">
                    Frequency
                  </div>
                  <div className="text-sm font-medium text-[#1e1e24]">
                    {formatFrequency(
                      recurringInvoice.frequency || 1,
                      recurringInvoice.frequencyUnit || "months",
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-[#767677] font-semibold">
                    Ends
                  </div>
                  <div className="text-sm font-medium text-[#1e1e24]">
                    {getEndDateLabel(recurringInvoice)}
                  </div>
                </div>
              </div>
            </div>

            {/* Editable fields */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="font-semibold text-gray-700">
                    Description
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder="Describe the service or product"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="dueDateType"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-semibold text-gray-700">
                      Payment Due
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger className="h-10 w-full rounded-lg border-gray-200">
                          <SelectValue placeholder="Select due date type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">On receipt</SelectItem>
                          <SelectItem value="net_days">
                            Within X days
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("dueDateType") === "net_days" && (
                <FormField
                  control={form.control}
                  name="dueDateDays"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="font-semibold text-gray-700">
                        Days until due
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={180}
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              field.onChange(0);
                            } else {
                              const parsed = parseInt(value, 10);
                              field.onChange(isNaN(parsed) ? 0 : parsed);
                            }
                          }}
                          className="h-10 w-24 rounded-lg border-gray-200"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </form>
        </Form>
      ) : (
        /* View Mode */
        <div className="space-y-4 sm:space-y-8">
          {/* Summary Card */}
          <div className="space-y-3 rounded-xl border border-[#f1f1f1] bg-[#f9f9f9] p-3 overflow-hidden">
            <div className="grid grid-cols-2 gap-x-2 gap-y-3">
              <div className="space-y-0.5 min-w-0 overflow-hidden">
                <div className="text-[10px] uppercase tracking-wide text-[#767677] font-semibold">
                  Amount
                </div>
                <div className="text-sm font-medium text-[#1e1e24] truncate">
                  {formatCurrencyAmount(
                    recurringInvoice.amount || 0,
                    recurringInvoice.currency || "usd",
                  )}
                </div>
              </div>
              <div className="space-y-0.5 min-w-0 overflow-hidden">
                <div className="text-[10px] uppercase tracking-wide text-[#767677] font-semibold">
                  Frequency
                </div>
                <div className="text-sm font-medium text-[#1e1e24] truncate">
                  {formatFrequency(
                    recurringInvoice.frequency || 1,
                    recurringInvoice.frequencyUnit || "months",
                  )}
                </div>
              </div>
              {isActive && (
                <div className="space-y-0.5 min-w-0 overflow-hidden">
                  <div className="text-[10px] uppercase tracking-wide text-[#767677] font-semibold">
                    Next Invoice
                  </div>
                  <div className="text-sm font-medium text-[#1e1e24] truncate">
                    {formatDate(recurringInvoice.nextInvoiceAt)}
                  </div>
                </div>
              )}
              <div className="space-y-0.5 min-w-0 overflow-hidden">
                <div className="text-[10px] uppercase tracking-wide text-[#767677] font-semibold">
                  {isActive ? "Ends" : "Ended"}
                </div>
                <div className="text-sm font-medium text-[#1e1e24] truncate">
                  {getEndDateLabel(recurringInvoice)}
                </div>
              </div>
              <div className="space-y-0.5 min-w-0 overflow-hidden">
                <div className="text-[10px] uppercase tracking-wide text-[#767677] font-semibold">
                  Payment Due
                </div>
                <div className="text-sm font-medium text-[#1e1e24] truncate">
                  {getPaymentDueLabel(
                    recurringInvoice.dueDateType,
                    recurringInvoice.dueDateDays,
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons (Active only) */}
            {isActive && !showCancelConfirm && (
              <div className="flex items-center gap-2 pt-2 border-t border-[#f1f1f1]">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditing(true);
                    setShowCancelConfirm(false);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={cancelMutation.isPending}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Cancel Invoice
                </Button>
              </div>
            )}
          </div>

          {/* Cancel Confirmation */}
          {showCancelConfirm && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-5 animate-in fade-in-0 duration-200">
              <div className="space-y-3">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <div className="font-semibold text-gray-900">
                  Cancel recurring invoice?
                </div>
                <div className="text-sm text-gray-600">
                  This will stop all future automatic invoices for this
                  customer. This action cannot be undone.
                </div>
                <div className="text-sm text-gray-500">
                  Already-issued invoices will not be affected.
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={cancelMutation.isPending}
                  >
                    Keep Invoice
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCancel}
                    disabled={cancelMutation.isPending}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {cancelMutation.isPending
                      ? "Canceling..."
                      : "Cancel Invoice"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {recurringInvoice.description && (
            <div className="space-y-2">
              <Typography size="sh2" className="text-[#1e1e24]">
                Description
              </Typography>
              <p className="text-sm text-[#767677]">
                {recurringInvoice.description}
              </p>
            </div>
          )}

          {/* Issued Invoices Section */}
          <div className="space-y-3">
            <Typography size="sh2" className="text-[#1e1e24]">
              Issued Invoices (
              {recurringInvoice.issuedInvoicesCount || issuedInvoices.length})
            </Typography>
            {issuedInvoices.length === 0 ? (
              <div className="text-left py-8 text-[#767677] text-sm">
                No invoices issued yet
              </div>
            ) : (
              <ScrollFadeWrapper>
              <Table bordered={false} className="w-full min-w-[500px]">
                <TableHeader className="bg-transparent">
                  <TableRow>
                    <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Invoice #
                    </TableHead>
                    <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Date
                    </TableHead>
                    <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Amount
                    </TableHead>
                    <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issuedInvoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      onClick={() => {
                        track("recurring_invoice_instance_clicked", {
                          recurring_invoice_id: recurringInvoice?.id,
                          invoice_id: invoice.id,
                          invoice_status: invoice.status,
                        });
                        onInvoiceClick?.(invoice.id);
                      }}
                      className="cursor-pointer hover:bg-[#fafafa] transition-colors"
                    >
                      <TableCell className="px-3 py-3 text-sm font-medium text-[#1e1e24]">
                        #{invoice.number || "N/A"}
                      </TableCell>
                      <TableCell className="px-3 py-3 text-sm text-[#767677]">
                        {formatDate(invoice.date)}
                      </TableCell>
                      <TableCell className="px-3 py-3 text-sm text-[#767677]">
                        {formatCurrencyAmount(
                          invoice.amount || 0,
                          invoice.currency || "usd",
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-3 text-sm">
                        <Badge
                          color={
                            invoice.status === "paid"
                              ? "green"
                              : invoice.status === "open"
                                ? "blue"
                                : invoice.status === "uncollectible"
                                  ? "yellow"
                                  : invoice.status === "void"
                                    ? "red"
                                    : "grey"
                          }
                          variant="opaque"
                        >
                          {invoice.status
                            ? invoice.status.charAt(0).toUpperCase() +
                              invoice.status.slice(1)
                            : "Unknown"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </ScrollFadeWrapper>
            )}
          </div>
        </div>
      )}
    </InvoiceDrawer>
  );
};

export default RecurringInvoiceDrawer;
